/**
 * Utility functions for Feline-SA
 */

import { format, formatDistanceToNow, differenceInMonths, differenceInWeeks } from 'date-fns';

// =====================
// DATE UTILITIES
// =====================

export const formatDate = (date, formatStr = 'dd MMM yyyy') => {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  return format(d, formatStr);
};

export const formatRelativeDate = (date) => {
  if (!date) return '';
  const d = date.toDate ? date.toDate() : new Date(date);
  return formatDistanceToNow(d, { addSuffix: true });
};

export const getCatAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';
  const dob = dateOfBirth.toDate ? dateOfBirth.toDate() : new Date(dateOfBirth);
  const months = differenceInMonths(new Date(), dob);
  
  if (months < 1) {
    const weeks = differenceInWeeks(new Date(), dob);
    return `${weeks} week${weeks !== 1 ? 's' : ''} old`;
  }
  
  if (months < 12) {
    return `${months} month${months !== 1 ? 's' : ''} old`;
  }
  
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  if (remainingMonths === 0) {
    return `${years} year${years !== 1 ? 's' : ''} old`;
  }
  
  return `${years} year${years !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''} old`;
};

// =====================
// CURRENCY UTILITIES
// =====================

export const formatCurrency = (amount, currency = 'ZAR') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  return new Intl.NumberFormat('en-ZA').format(num);
};

// =====================
// STATUS UTILITIES
// =====================

export const getStatusConfig = (status) => {
  const configs = {
    available: {
      label: 'Available',
      color: 'paw',
      bgClass: 'bg-paw-100',
      textClass: 'text-paw-700',
      dotClass: 'bg-paw-500'
    },
    reserved: {
      label: 'Reserved',
      color: 'amber',
      bgClass: 'bg-amber-100',
      textClass: 'text-amber-700',
      dotClass: 'bg-amber-500'
    },
    sold: {
      label: 'Sold',
      color: 'red',
      bgClass: 'bg-red-100',
      textClass: 'text-red-700',
      dotClass: 'bg-red-500'
    },
    not_for_sale: {
      label: 'Not for Sale',
      color: 'gray',
      bgClass: 'bg-gray-100',
      textClass: 'text-gray-700',
      dotClass: 'bg-gray-500'
    },
    pending: {
      label: 'Pending',
      color: 'amber',
      bgClass: 'bg-amber-100',
      textClass: 'text-amber-700',
      dotClass: 'bg-amber-500'
    },
    paid: {
      label: 'Paid',
      color: 'paw',
      bgClass: 'bg-paw-100',
      textClass: 'text-paw-700',
      dotClass: 'bg-paw-500'
    }
  };
  
  return configs[status] || configs.not_for_sale;
};

// =====================
// EMS CODE UTILITIES
// =====================

// EMS (Easy Mind System) code parser for cat colors
// Format: BREED color pattern [eye color] [with white]
// Example: MCO n 22 = Maine Coon, black, classic tabby

export const EMS_COLORS = {
  n: 'Black',
  a: 'Blue',
  b: 'Chocolate',
  c: 'Lilac',
  d: 'Red',
  e: 'Cream',
  f: 'Black Tortie',
  g: 'Blue Tortie',
  h: 'Chocolate Tortie',
  j: 'Lilac Tortie',
  o: 'Cinnamon',
  p: 'Fawn',
  q: 'Cinnamon Tortie',
  r: 'Fawn Tortie',
  w: 'White',
  s: 'Silver/Smoke'
};

export const EMS_PATTERNS = {
  '': 'Solid',
  '11': 'Shaded',
  '12': 'Shell',
  '21': 'Tabby unspecified',
  '22': 'Classic/Blotched Tabby',
  '23': 'Mackerel Tabby',
  '24': 'Spotted Tabby',
  '25': 'Ticked Tabby',
  '31': 'Burmese (Sepia)',
  '32': 'Tonkinese (Mink)',
  '33': 'Siamese (Pointed)'
};

export const parseEmsCode = (ems) => {
  if (!ems) return { breed: '', color: '', pattern: '', description: '' };
  
  const parts = ems.trim().toUpperCase().split(' ');
  const breed = parts[0] || '';
  const colorCode = parts[1]?.toLowerCase() || '';
  const patternCode = parts[2] || '';
  
  const color = EMS_COLORS[colorCode] || colorCode;
  const pattern = EMS_PATTERNS[patternCode] || '';
  
  const description = `${color}${pattern ? ` ${pattern}` : ''}`.trim();
  
  return { breed, color, pattern, description };
};

// =====================
// VALIDATION UTILITIES
// =====================

export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePhone = (phone) => {
  // SA phone number format
  const re = /^(\+27|0)[0-9]{9}$/;
  return re.test(phone.replace(/\s/g, ''));
};

export const validateMicrochip = (chip) => {
  // Standard 15-digit microchip
  const re = /^[0-9]{15}$/;
  return re.test(chip);
};

export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('27')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  
  if (cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  
  return phone;
};

// =====================
// COMMISSION CALCULATIONS
// =====================

export const COMMISSION_RATE = 0.10; // 10%
export const REGISTRATION_FEE_RATE = 0.10; // 10% service fee on registry

export const calculateCommission = (salePrice) => {
  return Math.round(salePrice * COMMISSION_RATE);
};

export const calculateRegistrationFee = (baseFee) => {
  return Math.round(baseFee * REGISTRATION_FEE_RATE);
};

// =====================
// SEARCH/FILTER UTILITIES
// =====================

export const filterCats = (cats, filters) => {
  return cats.filter(cat => {
    if (filters.breed && cat.breed !== filters.breed) return false;
    if (filters.sex && cat.sex !== filters.sex) return false;
    if (filters.status && cat.status !== filters.status) return false;
    if (filters.minPrice && cat.price < filters.minPrice) return false;
    if (filters.maxPrice && cat.price > filters.maxPrice) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const searchableFields = [cat.name, cat.breed, cat.color, cat.ems].join(' ').toLowerCase();
      if (!searchableFields.includes(search)) return false;
    }
    return true;
  });
};

// =====================
// FILE UTILITIES
// =====================

export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export const isImageFile = (filename) => {
  const ext = getFileExtension(filename).toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// =====================
// SLUG/ID UTILITIES
// =====================

export const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim();
};

export const generateId = (prefix = '') => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}_${timestamp}${random}` : `${timestamp}${random}`;
};

// =====================
// CLASSNAME UTILITY
// =====================

export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};
